import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = { class: "repair-bar" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 0,
  class: "error",
  role: "alert"
}
const _hoisted_5 = {
  class: "notice",
  role: "status"
}
const _hoisted_6 = ["href", "download"]

import { computed, onBeforeUnmount, ref } from "vue";
import type { GilUiIndexRepairResult } from "./gilUiIndexRepair";


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkspaceGilUiIndexRepair',
  props: {
    disabled: { type: Boolean }
  },
  emits: ["busy"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const fileInput = ref<HTMLInputElement>();
const busy = ref(false), error = ref("");
const result = ref<Omit<GilUiIndexRepairResult, "bytes">>();
const fileName = ref(""), downloadUrl = ref("");
const changedCount = computed(() => result.value?.rows.filter(row => row.changed).length ?? 0);
function clearDownload() {
  if (downloadUrl.value) URL.revokeObjectURL(downloadUrl.value);
  downloadUrl.value = "";
}
onBeforeUnmount(clearDownload);
async function readFile(event: Event) {
  const input = event.target as HTMLInputElement, file = input.files?.[0];
  input.value = "";
  if (!file || busy.value || props.disabled) return;
  clearDownload(); result.value = undefined; error.value = "";
  busy.value = true; emit("busy", true);
  try {
    if (!/\.gil$/i.test(file.name)) throw new Error("请选择 .gil 存档文件。");
    if (file.size > 100 * 1024 * 1024) throw new Error("GIL 文件超过 100 MB，请选择较小的存档。");
    const { repairGilUiIndices } = await import("./gilUiIndexRepair");
    const { bytes, ...preview } = repairGilUiIndices(await file.arrayBuffer());
    if (preview.rows.some(row => row.changed)) {
      downloadUrl.value = URL.createObjectURL(new Blob([bytes.buffer as ArrayBuffer], { type: "application/octet-stream" }));
    }
    fileName.value = file.name.replace(/\.gil$/i, "") + "_已修复索引.gil";
    // Retain only the preview; the download Blob owns the repaired archive bytes.
    result.value = preview;
  } catch (reason) { error.value = reason instanceof Error ? reason.message : String(reason); }
  finally { busy.value = false; emit("busy", false); }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: "ui-index-repair",
    "aria-label": "自动修复索引",
    "aria-busy": busy.value
  }, [
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("div", null, [
        _createElementVNode("strong", null, "自动修复索引"),
        _createElementVNode("p", null, "读取 GIL 中的确认提示、对话 UI 索引，修复五个客户端脚本中的对应变量。文件在本机处理，修复后下载新存档。")
      ], -1)),
      _createElementVNode("button", {
        type: "button",
        disabled: busy.value || __props.disabled,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (fileInput.value?.click()))
      }, _toDisplayString(busy.value ? '修复中…' : '读取 GIL 并修复'), 9, _hoisted_3),
      _createElementVNode("input", {
        ref_key: "fileInput",
        ref: fileInput,
        type: "file",
        accept: ".gil",
        hidden: "",
        "aria-label": "选择修复索引用 GIL 存档",
        onChange: readFile
      }, null, 544)
    ]),
    (error.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    (result.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("p", _hoisted_5, "已检查 " + _toDisplayString(result.value.rows.length) + " 项脚本变量，" + _toDisplayString(changedCount.value ? `${changedCount.value} 项索引已修复，请下载新存档并在千星编辑器中打开。` : '匹配到的索引均正确，无需修复。'), 1),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(result.value.ui, (control) => {
            return (_openBlock(), _createElementBlock("p", {
              key: control.name,
              class: "control-index"
            }, [
              _createTextVNode(_toDisplayString(control.name) + "：", 1),
              _createElementVNode("code", null, _toDisplayString(control.index), 1)
            ]))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(result.value.warnings, (warning) => {
            return (_openBlock(), _createElementBlock("p", {
              key: warning,
              class: "warning",
              role: "alert"
            }, _toDisplayString(warning), 1))
          }), 128)),
          _createElementVNode("details", null, [
            _cache[3] || (_cache[3] = _createElementVNode("summary", null, "查看脚本变量修复明细", -1)),
            _createElementVNode("ul", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(result.value.rows, (row) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: `${row.script}:${row.variable}`
                }, [
                  _createElementVNode("strong", null, _toDisplayString(row.script), 1),
                  _createElementVNode("small", null, "/" + _toDisplayString(row.path.replace(/^\/+/, '')), 1),
                  _createElementVNode("span", null, [
                    _createTextVNode(_toDisplayString(row.variable) + "：", 1),
                    _createElementVNode("code", null, _toDisplayString(row.previous.join(' / ')), 1),
                    _cache[2] || (_cache[2] = _createTextVNode(" → ", -1)),
                    _createElementVNode("code", null, _toDisplayString(row.index), 1),
                    _createTextVNode(_toDisplayString(row.changed ? '（已修复）' : '（已正确）'), 1)
                  ])
                ]))
              }), 128))
            ])
          ]),
          (downloadUrl.value)
            ? (_openBlock(), _createElementBlock("a", {
                key: 0,
                href: downloadUrl.value,
                download: fileName.value
              }, "下载修复后的 GIL", 8, _hoisted_6))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ], 8, _hoisted_1))
}
}

})